import React, { useMemo } from "react"
import clsx from "clsx"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import GridHeader from "./GridHeader"
import FooterRow from "./FooterRow"
import ProjectCell from "./ProjectCell"
import { EMPTY } from "../hooks/useCellIndex"
import { dayTone } from "../lib/days"
import { formatHours, sumHours } from "../lib/hours"

const ProjectRow = ({ project, days, template, cells, selection, highlighted, onRemove }) => {
  let total = 0
  const people = new Set()
  const rendered = days.map(day => {
    const items = cells.get(`${project.id}|${day.date}`) || EMPTY
    total += sumHours(items)
    items.forEach(item => people.add(item.user_id || "none"))
    const selectedKey = selection.size ? items.filter(item => selection.has(item.id)).map(item => item.id).join(",") : ""
    return (
      <ProjectCell key={day.date} projectId={project.id} date={day.date} tone={dayTone(day)} items={items} selectedKey={selectedKey} color={project.color} />
    )
  })

  return (
    <div className="grid group/row" style={{ gridTemplateColumns: template }}>
      <div className={clsx(
        "sticky left-0 z-[5] flex items-center gap-2.5 px-3 py-2 border-r border-b border-slate-200 min-w-0",
        highlighted ? "bg-brand-50" : "bg-white group-hover/row:bg-slate-50",
      )}>
        <span className="w-2.5 h-8 rounded-full shrink-0" style={{ backgroundColor: project.color }} />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5 min-w-0">
            {project.code && <span className="font-mono text-[10.5px] font-semibold text-slate-500 shrink-0">{project.code}</span>}
            <span className="text-[13px] font-semibold text-slate-800 truncate">{project.name}</span>
          </div>
          <div className="text-[11px] text-slate-400 truncate">{project.client || project.address}</div>
        </div>
        {onRemove && total === 0 && (
          <button type="button" onClick={onRemove} title={I18n.t("project_planning.actions.hide_row")} className="opacity-0 group-hover/row:opacity-100 text-slate-400 hover:text-slate-700 transition-opacity">
            <FontAwesomeIcon icon="xmark" className="text-[11px]" />
          </button>
        )}
      </div>
      {rendered}
      <div className="flex flex-col items-end justify-center gap-0.5 px-3 border-b border-slate-100 bg-white">
        <span className="text-[12.5px] font-semibold text-slate-800 tabular-nums">{formatHours(total)}</span>
        <span className="text-[10.5px] text-slate-400">{I18n.t("project_planning.people_count", { count: people.size })}</span>
      </div>
    </div>
  )
}

const ProjectGrid = ({ board, projects, extraIds, cells, selection, template, density, highlightProjectId, onAddProject, onRemoveExtra, canWrite }) => {
  const counts = useMemo(() => {
    const result = {}
    board.days.forEach(day => {
      result[day.date] = new Set(board.assignments.filter(item => item.date === day.date && item.user_id).map(item => item.user_id)).size
    })
    return result
  }, [board.days, board.assignments])

  const total = useMemo(() => sumHours(board.assignments.filter(item => projects.some(project => project.id === item.project_id))), [board.assignments, projects])

  return (
    <div className="min-w-[var(--grid-min)]">
      <GridHeader days={board.days} template={template} density={density} firstLabel={I18n.t("project_planning.columns.projects", { count: projects.length })} />

      {projects.map(project => (
        <ProjectRow
          key={project.id}
          project={project}
          days={board.days}
          template={template}
          cells={cells}
          selection={selection}
          highlighted={project.id === highlightProjectId}
          onRemove={extraIds.includes(project.id) ? () => onRemoveExtra(project.id) : null}
        />
      ))}

      {projects.length === 0 && (
        <div className="flex flex-col items-center px-6 py-12 text-center">
          <span className="grid place-items-center w-11 h-11 rounded-full bg-slate-100 text-slate-400 mb-3">
            <FontAwesomeIcon icon="calendar-days" className="text-[16px]" />
          </span>
          <div className="text-[14px] font-semibold text-slate-800">{I18n.t("project_planning.projects_view.empty_title")}</div>
          <p className="mt-1 text-[12.5px] text-slate-500 max-w-sm">{I18n.t("project_planning.projects_view.empty_body")}</p>
        </div>
      )}

      {canWrite && (
        <div className="grid" style={{ gridTemplateColumns: template }}>
          <div className="sticky left-0 z-[5] px-3 py-2 bg-white border-r border-b border-slate-200">
            <button
              type="button"
              onClick={event => onAddProject(event.currentTarget.getBoundingClientRect())}
              className="inline-flex items-center gap-1.5 h-7 px-2.5 rounded-full border border-dashed border-slate-300 text-[12px] font-medium text-slate-500 hover:border-solid hover:border-brand-500 hover:text-brand-600 hover:bg-brand-50 transition-colors"
            >
              <FontAwesomeIcon icon="plus" className="text-[10px]" />
              {I18n.t("project_planning.projects_view.add")}
            </button>
          </div>
          <div className="border-b border-slate-100" style={{ gridColumn: `span ${board.days.length + 1}` }} />
        </div>
      )}

      <FooterRow days={board.days} template={template} label={I18n.t("project_planning.footer.people")} counts={counts} total={total} />
    </div>
  )
}

export default ProjectGrid
